import React, { useMemo } from 'react';
import '../restaurant/css/AgePricingEditor.css';

const WEEKDAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const DAY_LABELS = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
};

const DEFAULT_OPEN = '09:00';
const DEFAULT_CLOSE = '21:00';

export function emptyStoreOpeningHours() {
  return WEEKDAYS.reduce((acc, day) => {
    acc[day] = {
      closed: false,
      openTime: DEFAULT_OPEN,
      closeTime: DEFAULT_CLOSE,
    };
    return acc;
  }, {});
}

export function normalizeStoreOpeningHours(payload) {
  const base = emptyStoreOpeningHours();
  if (!payload || typeof payload !== 'object') {
    return base;
  }
  WEEKDAYS.forEach((day) => {
    const entry = payload[day] || payload[day.toLowerCase()];
    if (!entry || typeof entry !== 'object') {
      return;
    }
    base[day] = {
      closed: Boolean(entry.closed ?? entry.isClosed),
      openTime: String(entry.openTime || entry.open_time || DEFAULT_OPEN).slice(0, 5),
      closeTime: String(entry.closeTime || entry.close_time || DEFAULT_CLOSE).slice(0, 5),
    };
  });
  return base;
}

/**
 * Weekday store open / close hours.
 */
export default function StoreOpeningHoursEditor({
  value,
  onChange,
  disabled = false,
  title = 'Store timings',
  hint = 'Set the hours this store is open each day. Mark a day as closed when the store does not operate.',
}) {
  const hours = useMemo(() => normalizeStoreOpeningHours(value), [value]);

  const updateDay = (day, patch) => {
    onChange?.({
      ...hours,
      [day]: {
        ...hours[day],
        ...patch,
      },
    });
  };

  return (
    <div className="age-pricing-editor meal-delivery-editor">
      <div className="age-pricing-editor__header">
        <label className="age-pricing-editor__title">{title}</label>
        <p className="age-pricing-editor__hint">{hint}</p>
      </div>
      <div className="age-pricing-editor__table-wrap">
        <table className="age-pricing-editor__table">
          <thead>
            <tr>
              <th>Day</th>
              <th>Closed</th>
              <th>Opens</th>
              <th>Closes</th>
            </tr>
          </thead>
          <tbody>
            {WEEKDAYS.map((day) => {
              const row = hours[day];
              return (
                <tr key={day}>
                  <td>{DAY_LABELS[day]}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={Boolean(row.closed)}
                      disabled={disabled}
                      onChange={(e) => updateDay(day, { closed: e.target.checked })}
                      aria-label={`${DAY_LABELS[day]} closed`}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={row.openTime}
                      disabled={disabled || row.closed}
                      onChange={(e) => updateDay(day, { openTime: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={row.closeTime}
                      disabled={disabled || row.closed}
                      onChange={(e) => updateDay(day, { closeTime: e.target.value })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
